import fs from 'node:fs'
import path from 'node:path'
import { writeConfigFile } from '@core-engine/config/serialize.js'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import {
  type LoadedSchema,
  listSchemaNames,
  loadSchema,
  validateLoadedSchema
} from '@core-engine/schema/schema-reader.js'
import { ensureSnapshotGitFiles } from '@core-engine/snapshots/gitignore.js'
import {
  BARREL_SCOPE,
  deleteSnapshot,
  hasTombstone,
  loadManifest
} from '@core-engine/snapshots/store.js'
import { appendEnvVars, removeEnvVars } from '@core-engine/utils/env.js'
import type { PackageManager } from '@core-engine/utils/package-manager.js'
import {
  applyAdminNamespaceToContent,
  applyAdminNamespaceToPath
} from '@next-adapter/config/namespace.js'
import { resolveProjectPaths } from '@next-adapter/config/paths.js'
import { runFormPipeline } from '@next-adapter/generators/form-pipeline/index.js'
import { runEntityPipeline, runSinglePipeline } from '@next-adapter/generators/index.js'
import {
  getDependencyPlan,
  installDependenciesAsync,
  removeDependenciesAsync
} from '@next-adapter/init/scaffolders/dependencies.js'
import { getIntegrationDefinition } from '@next-adapter/integration-registry.js'
import {
  formatUnknownPluginMessage,
  getPluginDefinition,
  isPluginId,
  listPluginDefinitions
} from '@next-adapter/plugin-registry.js'
import { readPluginTemplate } from '@next-adapter/plugin-template-reader.js'
import { applyGeneratedFiles } from '@next-adapter/snapshots/apply.js'
import { renderBarrelFiles } from '@next-adapter/snapshots/barrels.js'
import { readTemplate } from '@next-adapter/template-reader.js'
import {
  deleteInstalledPluginManifest,
  loadInstalledPluginManifest,
  saveInstalledPluginManifest
} from '@plugin-engine/manifests.js'
import {
  getSchemaOwner,
  loadSchemaOwnershipIndex,
  removeSchemaOwner,
  setSchemaOwner
} from '@plugin-engine/ownership.js'
import type {
  BetterstartPluginDefinition,
  BetterstartPluginId,
  InstalledPluginManifest
} from '@plugin-engine/types.js'

interface ConfigurePluginResult {
  configured: boolean
  addedEnvKeys: string[]
  updatedEnvKeys: string[]
}

export interface InstallPluginsOptions {
  cwd: string
  config: ResolvedBetterstartConfig
  pm: PackageManager
  pluginIds: BetterstartPluginId[]
  interactive: boolean
  includeBiome: boolean
}

export interface InstallPluginsResult {
  installed: BetterstartPluginId[]
  skipped: BetterstartPluginId[]
  warnings: string[]
  config: ResolvedBetterstartConfig
}

export interface RemovePluginsOptions {
  cwd: string
  config: ResolvedBetterstartConfig
  pm: PackageManager
  pluginIds: BetterstartPluginId[]
}

export interface RemovePluginsResult {
  removed: BetterstartPluginId[]
  warnings: string[]
  config: ResolvedBetterstartConfig
}

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values))
}

function normalizeInstalledPlugins(config: ResolvedBetterstartConfig): BetterstartPluginId[] {
  return unique((config.plugins.installed ?? []).filter(isPluginId)).sort()
}

function resolvePluginInstallOrder(pluginIds: BetterstartPluginId[]): BetterstartPluginId[] {
  const ordered: BetterstartPluginId[] = []
  const visiting = new Set<BetterstartPluginId>()
  const visited = new Set<BetterstartPluginId>()

  function visit(pluginId: BetterstartPluginId): void {
    if (visited.has(pluginId)) {
      return
    }
    if (visiting.has(pluginId)) {
      throw new Error(`Circular plugin dependency detected at "${pluginId}".`)
    }

    visiting.add(pluginId)
    for (const dependency of getPluginDefinition(pluginId).dependencies) {
      visit(dependency)
    }
    visiting.delete(pluginId)
    visited.add(pluginId)
    ordered.push(pluginId)
  }

  for (const pluginId of pluginIds) {
    visit(pluginId)
  }

  return ordered
}

function getSchemasDir(cwd: string, config: ResolvedBetterstartConfig): string {
  const paths = resolveProjectPaths(config)
  return path.join(cwd, ...paths.schemasDir.split('/'))
}

function runLoadedPipeline(
  loaded: LoadedSchema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  outputNamespace?: string
) {
  if (loaded.type === 'form') {
    return runFormPipeline(loaded.schema, cwd, config, {
      force: false,
      silent: true,
      outputNamespace
    })
  }

  if (loaded.type === 'single') {
    return runSinglePipeline(loaded.schema, cwd, config, {
      force: false,
      silent: true,
      outputNamespace
    })
  }

  return runEntityPipeline(loaded.schema, cwd, config, {
    force: false,
    silent: true,
    outputNamespace
  })
}

async function applyGeneratedSchema(
  cwd: string,
  config: ResolvedBetterstartConfig,
  loaded: LoadedSchema,
  outputNamespace?: string
): Promise<void> {
  const validationErrors = validateLoadedSchema(loaded)
  if (validationErrors.length > 0) {
    throw new Error(validationErrors.join('; '))
  }

  const pipelineResult = runLoadedPipeline(loaded, cwd, config, outputNamespace)
  if (!pipelineResult.success) {
    throw new Error(pipelineResult.errors.join('; '))
  }

  await applyGeneratedFiles({
    cwd,
    config,
    scope: loaded.schema.name,
    schemaJson: loaded.schema,
    generatedFiles: pipelineResult.files,
    force: false,
    interactive: false
  })
}

async function regenerateBarrels(cwd: string, config: ResolvedBetterstartConfig): Promise<void> {
  await applyGeneratedFiles({
    cwd,
    config,
    scope: BARREL_SCOPE,
    schemaJson: { name: BARREL_SCOPE },
    generatedFiles: renderBarrelFiles(cwd, config),
    force: false,
    interactive: false
  })
}

function resolvePluginProjectPath(config: ResolvedBetterstartConfig, outputPath: string): string {
  const namespacedOutputPath = applyAdminNamespaceToPath(
    outputPath,
    config.frameworkConfig.next.namespace
  )
  return path.posix.join(config.paths.admin.replace(/^\.\//, ''), namespacedOutputPath)
}

function namespacePluginContent(config: ResolvedBetterstartConfig, content: string): string {
  return applyAdminNamespaceToContent(content, config.frameworkConfig.next.namespace)
}

function flattenEnvKeys(definition: BetterstartPluginDefinition): string[] {
  return definition.envSections.flatMap((section) => section.vars.map((entry) => entry.key))
}

function normalizeManagedFileContent(content: string): string {
  return content.replace(/\r\n/g, '\n')
}

function tryReadCoreManagedTemplate(relativeOutputPath: string): string | null {
  try {
    return readTemplate(relativeOutputPath)
  } catch {
    return null
  }
}

function configurePlugin(
  definition: BetterstartPluginDefinition,
  cwd: string
): ConfigurePluginResult {
  const envResult = appendEnvVars(cwd, definition.envSections)
  return {
    configured: true,
    addedEnvKeys: envResult.added,
    updatedEnvKeys: envResult.updated
  }
}

function ensurePluginSchemaTargetsAvailable(
  cwd: string,
  config: ResolvedBetterstartConfig,
  definition: BetterstartPluginDefinition
): void {
  const schemasDir = getSchemasDir(cwd, config)

  for (const schemaFile of definition.schemaFiles) {
    const schemaName = schemaFile.replace(/\.json$/, '')
    const destinationPath = path.join(schemasDir, schemaFile)
    if (!fs.existsSync(destinationPath)) {
      continue
    }

    const owner = getSchemaOwner(cwd, schemaName)
    if (owner && owner !== `plugin:${definition.id}`) {
      throw new Error(
        `Schema "${schemaName}" is already owned by "${owner}". Remove or rename it before installing ${definition.id}.`
      )
    }

    if (!owner) {
      throw new Error(
        `Schema "${schemaName}" already exists and is not plugin-owned. Remove or rename it before installing ${definition.id}.`
      )
    }
  }
}

function ensurePluginDirectFileTargetsAvailable(
  cwd: string,
  config: ResolvedBetterstartConfig,
  definition: BetterstartPluginDefinition
): void {
  for (const file of definition.files) {
    const projectRelativePath = resolvePluginProjectPath(config, file.outputPath)
    const fullPath = path.join(cwd, ...projectRelativePath.split('/'))

    if (!fs.existsSync(fullPath)) {
      continue
    }

    const stats = fs.statSync(fullPath)
    if (!stats.isFile()) {
      throw new Error(
        `Installing ${definition.id} would overwrite "${projectRelativePath}", but it is not a file. Remove or rename it before continuing.`
      )
    }

    const existingContent = normalizeManagedFileContent(fs.readFileSync(fullPath, 'utf-8'))
    const pluginTemplate = normalizeManagedFileContent(
      namespacePluginContent(config, readPluginTemplate(definition.id, file.templatePath))
    )

    if (existingContent === pluginTemplate) {
      continue
    }

    const coreTemplate = tryReadCoreManagedTemplate(file.outputPath)
    if (
      coreTemplate &&
      existingContent === normalizeManagedFileContent(namespacePluginContent(config, coreTemplate))
    ) {
      continue
    }

    throw new Error(
      `Installing ${definition.id} would overwrite existing file "${projectRelativePath}". Rename, remove, or reset it to the managed template before installing ${definition.id}.`
    )
  }
}

function writePluginDirectFiles(
  cwd: string,
  config: ResolvedBetterstartConfig,
  definition: BetterstartPluginDefinition
): string[] {
  const writtenFiles: string[] = []

  for (const file of definition.files) {
    const projectRelativePath = resolvePluginProjectPath(config, file.outputPath)
    const fullPath = path.join(cwd, ...projectRelativePath.split('/'))
    fs.mkdirSync(path.dirname(fullPath), { recursive: true })
    fs.writeFileSync(
      fullPath,
      namespacePluginContent(config, readPluginTemplate(definition.id, file.templatePath)),
      'utf-8'
    )
    writtenFiles.push(projectRelativePath)
  }

  return writtenFiles
}

function toProjectAbsolutePath(cwd: string, filePath: string): string {
  return path.isAbsolute(filePath) ? filePath : path.join(cwd, ...filePath.split('/'))
}

function isInsideDir(child: string, parent: string): boolean {
  const relative = path.relative(parent, child)
  return relative === '' || (!relative.startsWith('..') && !path.isAbsolute(relative))
}

function cleanupEmptyDirs(
  cwd: string,
  deletedPaths: string[],
  config: ResolvedBetterstartConfig
): void {
  const paths = resolveProjectPaths(config)
  const projectRoot = path.resolve(cwd)
  const stopRoots = new Set([
    projectRoot,
    toProjectAbsolutePath(cwd, paths.adminDir),
    toProjectAbsolutePath(cwd, paths.pagesDir),
    toProjectAbsolutePath(cwd, paths.schemasDir),
    path.join(projectRoot, '.betterstart'),
    path.join(projectRoot, '.betterstart', 'plugins'),
    path.join(projectRoot, '.betterstart', 'snapshots')
  ])

  for (const deletedPath of deletedPaths) {
    let currentDir = path.dirname(toProjectAbsolutePath(cwd, deletedPath))

    while (isInsideDir(currentDir, projectRoot) && !stopRoots.has(currentDir)) {
      if (!fs.existsSync(currentDir)) {
        currentDir = path.dirname(currentDir)
        continue
      }

      if (fs.readdirSync(currentDir).length > 0) {
        break
      }

      fs.rmdirSync(currentDir)
      currentDir = path.dirname(currentDir)
    }
  }
}

function restoreOrDeletePluginFile(
  cwd: string,
  config: ResolvedBetterstartConfig,
  relativeOutputPath: string
): string | null {
  const projectRelativePath = resolvePluginProjectPath(config, relativeOutputPath)
  const fullPath = toProjectAbsolutePath(cwd, projectRelativePath)

  try {
    const coreTemplate = readTemplate(relativeOutputPath)
    fs.mkdirSync(path.dirname(fullPath), { recursive: true })
    fs.writeFileSync(fullPath, namespacePluginContent(config, coreTemplate), 'utf-8')
    return null
  } catch {
    const existed = fs.existsSync(fullPath)
    fs.rmSync(fullPath, { force: true })
    return existed ? projectRelativePath : null
  }
}

function writePluginSchemas(
  cwd: string,
  config: ResolvedBetterstartConfig,
  definition: BetterstartPluginDefinition
): string[] {
  const schemasDir = getSchemasDir(cwd, config)
  const writtenSchemaNames: string[] = []

  for (const schemaFile of definition.schemaFiles) {
    const outputPath = path.join(schemasDir, schemaFile)
    fs.mkdirSync(path.dirname(outputPath), { recursive: true })
    fs.writeFileSync(
      outputPath,
      readPluginTemplate(definition.id, `schemas/${schemaFile}`),
      'utf-8'
    )
    writtenSchemaNames.push(schemaFile.replace(/\.json$/, ''))
  }

  return writtenSchemaNames
}

function removePath(cwd: string, filePath: string): boolean {
  const fullPath = toProjectAbsolutePath(cwd, filePath)
  const existed = fs.existsSync(fullPath)
  fs.rmSync(fullPath, { recursive: true, force: true })
  return existed
}

function removePluginSchemas(
  cwd: string,
  config: ResolvedBetterstartConfig,
  manifest: InstalledPluginManifest
): string[] {
  const paths = resolveProjectPaths(config)
  const deletedPaths: string[] = []
  const manifestsBySchema = new Map<string, NonNullable<ReturnType<typeof loadManifest>>>()
  const missingManifests: string[] = []

  for (const schemaName of manifest.ownedSchemas) {
    const scopeManifest = loadManifest(cwd, schemaName)
    if (scopeManifest) {
      manifestsBySchema.set(schemaName, scopeManifest)
    } else {
      missingManifests.push(schemaName)
    }
  }

  if (missingManifests.length > 0) {
    throw new Error(
      `Cannot remove ${manifest.id}. Missing snapshot manifest${
        missingManifests.length === 1 ? '' : 's'
      } for plugin-owned schema${missingManifests.length === 1 ? '' : 's'}: ${missingManifests.join(
        ', '
      )}. Re-run \`pnpm betterstart init --force\` to restore snapshot state before removing the plugin.`
    )
  }

  for (const schemaName of manifest.ownedSchemas) {
    const scopeManifest = manifestsBySchema.get(schemaName)
    if (scopeManifest) {
      for (const filePath of [
        ...scopeManifest.files.map((entry) => entry.path),
        ...scopeManifest.skipped
      ]) {
        if (removePath(cwd, filePath)) {
          deletedPaths.push(filePath)
        }
      }
      deleteSnapshot(cwd, schemaName)
    }

    const schemaPath = path.posix.join(paths.schemasDir, `${schemaName}.json`)
    const formsSchemaPath = path.posix.join(paths.schemasDir, 'forms', `${schemaName}.json`)
    if (removePath(cwd, schemaPath)) {
      deletedPaths.push(schemaPath)
    }
    if (removePath(cwd, formsSchemaPath)) {
      deletedPaths.push(formsSchemaPath)
    }
    if (hasTombstone(cwd, schemaName)) {
      fs.rmSync(path.join(cwd, '.betterstart', 'snapshots', '_removed', schemaName), {
        force: true
      })
    }
    removeSchemaOwner(cwd, schemaName)
  }

  return deletedPaths
}

function collectSchemaRelationshipTargets(loaded: LoadedSchema): string[] {
  const targets = new Set<string>()

  function walkFields(fields: unknown): void {
    if (!Array.isArray(fields)) return

    for (const field of fields) {
      if (!field || typeof field !== 'object') {
        continue
      }

      const fieldRecord = field as Record<string, unknown>
      const relationship =
        typeof fieldRecord.relationship === 'string' ? fieldRecord.relationship : undefined
      if (relationship) {
        targets.add(relationship)
      }

      walkFields(fieldRecord.fields)

      if (Array.isArray(fieldRecord.tabs)) {
        for (const tab of fieldRecord.tabs) {
          if (!tab || typeof tab !== 'object') {
            continue
          }
          walkFields((tab as Record<string, unknown>).fields)
        }
      }
    }
  }

  if (loaded.type === 'form') {
    walkFields(loaded.schema.fields)
    if (Array.isArray(loaded.schema.steps)) {
      for (const step of loaded.schema.steps) {
        walkFields(step.fields)
      }
    }
  } else {
    walkFields(loaded.schema.fields)
  }

  return Array.from(targets)
}

function findBlockingSchemaDependencies(
  cwd: string,
  config: ResolvedBetterstartConfig,
  ownedSchemas: string[]
): string[] {
  const blockers: string[] = []
  const schemasDir = getSchemasDir(cwd, config)
  const ownership = loadSchemaOwnershipIndex(cwd)

  for (const schemaName of listSchemaNames(schemasDir)) {
    if (ownership.schemas[schemaName] !== 'user') {
      continue
    }

    const loaded = loadSchema(schemasDir, schemaName)
    const relationshipTargets = collectSchemaRelationshipTargets(loaded).filter((target) =>
      ownedSchemas.includes(target)
    )

    if (relationshipTargets.length > 0) {
      blockers.push(
        `${schemaName} references plugin-owned schema(s): ${relationshipTargets.join(', ')}`
      )
    }
  }

  return blockers
}

function getUnusedPluginDependencies(
  remainingPlugins: BetterstartPluginId[],
  removedPluginId: BetterstartPluginId,
  installedIntegrations: string[]
): { dependencies: string[]; devDependencies: string[] } {
  const remainingDefinitions = remainingPlugins.map((pluginId) => getPluginDefinition(pluginId))
  const integrationDefinitions = installedIntegrations.map((integrationId) =>
    getIntegrationDefinition(integrationId)
  )
  const remainingDependencies = new Set([
    ...remainingDefinitions.flatMap((definition) => definition.packageDependencies),
    ...integrationDefinitions.flatMap((definition) => definition.packageDependencies)
  ])
  const remainingDevDependencies = new Set([
    ...remainingDefinitions.flatMap((definition) => definition.devDependencies),
    ...integrationDefinitions.flatMap((definition) => definition.devDependencies)
  ])

  const corePlan = getDependencyPlan([], [], false)
  const removedDefinition = getPluginDefinition(removedPluginId)

  return {
    dependencies: removedDefinition.packageDependencies.filter(
      (dependency) =>
        !remainingDependencies.has(dependency) && !corePlan.dependencies.includes(dependency)
    ),
    devDependencies: removedDefinition.devDependencies.filter(
      (dependency) =>
        !remainingDevDependencies.has(dependency) && !corePlan.devDependencies.includes(dependency)
    )
  }
}

export function parsePluginList(value: string | undefined): BetterstartPluginId[] {
  if (!value) {
    return []
  }

  const pluginIds = value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)

  const invalid = pluginIds.filter((pluginId) => !isPluginId(pluginId))
  if (invalid.length > 0) {
    throw new Error(formatUnknownPluginMessage(invalid))
  }

  return unique(pluginIds as BetterstartPluginId[])
}

export function getInstalledPlugins(config: ResolvedBetterstartConfig): BetterstartPluginId[] {
  return normalizeInstalledPlugins(config)
}

export function syncInstalledPluginManifests(cwd: string, config: ResolvedBetterstartConfig): void {
  for (const pluginId of normalizeInstalledPlugins(config)) {
    const manifest = loadInstalledPluginManifest(cwd, pluginId)
    if (!manifest) {
      continue
    }

    const definition = getPluginDefinition(pluginId)
    const nextManifest: InstalledPluginManifest = {
      ...manifest,
      version: definition.version,
      kind: definition.kind,
      ownedFiles: definition.files.map((file) => resolvePluginProjectPath(config, file.outputPath)),
      packageDependencies: definition.packageDependencies,
      devDependencies: definition.devDependencies,
      dependencies: definition.dependencies,
      conflicts: definition.conflicts
    }

    if (JSON.stringify(nextManifest) !== JSON.stringify(manifest)) {
      saveInstalledPluginManifest(cwd, nextManifest)
    }
  }
}

export function ensureCoreSchemaOwnership(cwd: string, schemaName: string): void {
  setSchemaOwner(cwd, schemaName, 'core')
}

export function ensureUserSchemaOwnership(cwd: string, schemaName: string): void {
  if (schemaName === 'settings') {
    setSchemaOwner(cwd, schemaName, 'core')
    return
  }

  const owner = getSchemaOwner(cwd, schemaName)
  if (!owner) {
    setSchemaOwner(cwd, schemaName, 'user')
  }
}

export async function installPlugins({
  cwd,
  config,
  pm,
  pluginIds,
  includeBiome
}: InstallPluginsOptions): Promise<InstallPluginsResult> {
  const orderedPluginIds = resolvePluginInstallOrder(pluginIds)
  const installedPluginIds = normalizeInstalledPlugins(config)
  const skipped: BetterstartPluginId[] = []
  const installed: BetterstartPluginId[] = []
  const warnings: string[] = []

  ensureSnapshotGitFiles(cwd)

  for (const pluginId of orderedPluginIds) {
    if (installedPluginIds.includes(pluginId)) {
      skipped.push(pluginId)
      continue
    }

    const definition = getPluginDefinition(pluginId)
    ensurePluginSchemaTargetsAvailable(cwd, config, definition)
    ensurePluginDirectFileTargetsAvailable(cwd, config, definition)

    const configureResult = configurePlugin(definition, cwd)
    const dependencyPlan = getDependencyPlan(
      [...installedPluginIds, pluginId],
      config.integrations.installed,
      includeBiome
    )
    const currentPlan = getDependencyPlan(
      installedPluginIds,
      config.integrations.installed,
      includeBiome
    )

    const dependencyDiff = {
      dependencies: dependencyPlan.dependencies.filter(
        (dependency) => !currentPlan.dependencies.includes(dependency)
      ),
      devDependencies: dependencyPlan.devDependencies.filter(
        (dependency) => !currentPlan.devDependencies.includes(dependency)
      )
    }

    if (dependencyDiff.dependencies.length > 0 || dependencyDiff.devDependencies.length > 0) {
      const result = await installDependenciesAsync({
        cwd,
        pm,
        dependencies: dependencyDiff.dependencies,
        devDependencies: dependencyDiff.devDependencies
      })

      if (!result.success) {
        throw new Error(result.error ?? `Failed to install dependencies for ${pluginId}.`)
      }
    }

    const writtenFiles = writePluginDirectFiles(cwd, config, definition)
    const writtenSchemas = writePluginSchemas(cwd, config, definition)

    for (const schemaName of writtenSchemas) {
      const loaded = loadSchema(getSchemasDir(cwd, config), schemaName)
      await applyGeneratedSchema(cwd, config, loaded, pluginId)
      setSchemaOwner(cwd, schemaName, `plugin:${pluginId}`)
    }

    if (writtenSchemas.length > 0) {
      await regenerateBarrels(cwd, config)
    }

    installedPluginIds.push(pluginId)
    config.plugins.installed = unique(installedPluginIds).sort()

    const manifest: InstalledPluginManifest = {
      id: pluginId,
      version: definition.version,
      kind: definition.kind,
      ownedSchemas: writtenSchemas,
      ownedFiles: writtenFiles,
      envKeys: flattenEnvKeys(definition),
      packageDependencies: definition.packageDependencies,
      devDependencies: definition.devDependencies,
      configured: configureResult.configured,
      dependencies: definition.dependencies,
      conflicts: definition.conflicts,
      installedAt: new Date().toISOString()
    }

    saveInstalledPluginManifest(cwd, manifest)
    writeConfigFile(cwd, config)
    installed.push(pluginId)
  }

  return {
    installed,
    skipped,
    warnings,
    config
  }
}

export async function removePlugins({
  cwd,
  config,
  pm,
  pluginIds
}: RemovePluginsOptions): Promise<RemovePluginsResult> {
  const installedPlugins = normalizeInstalledPlugins(config)
  const removed: BetterstartPluginId[] = []
  const warnings: string[] = []

  for (const pluginId of pluginIds) {
    if (!installedPlugins.includes(pluginId)) {
      warnings.push(`${pluginId} is not installed.`)
      continue
    }

    const manifest = loadInstalledPluginManifest(cwd, pluginId)
    if (!manifest) {
      throw new Error(`Missing manifest for installed plugin "${pluginId}".`)
    }

    const dependents = installedPlugins.filter((installedPluginId) => {
      if (installedPluginId === pluginId) {
        return false
      }

      return getPluginDefinition(installedPluginId).dependencies.includes(pluginId)
    })

    if (dependents.length > 0) {
      throw new Error(
        `Cannot remove ${pluginId}. The following plugins depend on it: ${dependents.join(', ')}`
      )
    }

    const blockers = findBlockingSchemaDependencies(cwd, config, manifest.ownedSchemas)
    if (blockers.length > 0) {
      throw new Error(`Cannot remove ${pluginId}:\n- ${blockers.join('\n- ')}`)
    }

    const deletedPaths = removePluginSchemas(cwd, config, manifest)

    for (const filePath of manifest.ownedFiles) {
      const relativeOutputPath = filePath.replace(`${config.paths.admin.replace(/^\.\//, '')}/`, '')
      const deletedPath = restoreOrDeletePluginFile(cwd, config, relativeOutputPath)
      if (deletedPath) {
        deletedPaths.push(deletedPath)
      }
    }

    deleteInstalledPluginManifest(cwd, pluginId)
    const nextInstalledPlugins = installedPlugins.filter(
      (installedPluginId) => installedPluginId !== pluginId
    )
    installedPlugins.splice(0, installedPlugins.length, ...nextInstalledPlugins)
    config.plugins.installed = [...nextInstalledPlugins].sort()

    const remainingPlugins = normalizeInstalledPlugins(config)
    const dependencyDiff = getUnusedPluginDependencies(
      remainingPlugins,
      pluginId,
      config.integrations.installed
    )

    if (dependencyDiff.dependencies.length > 0 || dependencyDiff.devDependencies.length > 0) {
      const result = await removeDependenciesAsync({
        cwd,
        pm,
        dependencies: dependencyDiff.dependencies,
        devDependencies: dependencyDiff.devDependencies
      })

      if (!result.success) {
        warnings.push(result.error ?? `Failed to remove dependencies for ${pluginId}.`)
      }
    }

    const usedEnvKeys = new Set(
      remainingPlugins.flatMap((installedPluginId) =>
        flattenEnvKeys(getPluginDefinition(installedPluginId))
      )
    )
    const removableEnvKeys = manifest.envKeys.filter((envKey) => !usedEnvKeys.has(envKey))
    if (removableEnvKeys.length > 0) {
      removeEnvVars(cwd, removableEnvKeys)
    }

    if (manifest.ownedSchemas.length > 0) {
      await regenerateBarrels(cwd, config)
    }

    cleanupEmptyDirs(cwd, deletedPaths, config)

    writeConfigFile(cwd, config)
    removed.push(pluginId)
  }

  return {
    removed,
    warnings,
    config
  }
}

export function listAvailablePlugins() {
  return listPluginDefinitions()
}
